/**
 * Eingangsrechnungen unter Belege (R93–R97, nur leitung/admin):
 * Upload per Drag & Drop → Vorschau der erkannten Daten mit Prüfergebnis → Prüfung bestätigen → Festschreiben
 * (→ BuchhaltungsButler über die Outbox). Liste mit Status, Detail mit Positionen, Abgleich je Tag und PDF,
 * Kennzahl „Kosten pro Paket“.
 */
import { Fragment, useEffect, useRef, useState, type DragEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ABGLEICH_TEXT, EINGANG_KATEGORIE_TEXT, EINGANG_LIEFERANT_TEXT, EINGANG_STATUS, EINGANG_STATUS_TEXT,
  type EingangStatus, type EingangsrechnungDetail, type KostenMonat,
} from '@storeos/contracts';
import { ApiError } from '../api';
import {
  eingangAbgleich, eingangFestschreiben, eingangHochladen, eingangKennzahlen, eingangPdfBlob, eingangPruefen, eingangsrechnung, eingangsrechnungen, eingangVerwerfen,
} from '../api/eingang';
import { datum, datumZeit, euro } from '../components/format';
import { Darf, Dialog, DialogFormular, Feld, Filterleiste, Karte, Seitenkopf, Tabelle, useToast, Zustand } from '../components/ui';

const MAX_MB = 10;
const STATUS_KLASSE: Record<EingangStatus, string> = {
  hochgeladen: 'neutral', klaerfall: 'fehler-badge', geprueft: 'warn', festgeschrieben: 'er-fest', an_buchhaltung: 'er-buch', verworfen: 'er-verworfen',
};
export function EingangStatusBadge({ status }: { status: EingangStatus }) {
  return <span className={`badge ${STATUS_KLASSE[status]}`} data-testid="er-status" data-status={status}>{EINGANG_STATUS_TEXT[status]}</span>;
}
const monat = (ym: string | null) => (ym ? new Date(`${ym}-01T12:00:00`).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }) : '–');
const prozent = (bp: number | null) => (bp === null ? '–' : `${(bp / 100).toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`);

export function EingangsrechnungenSeite() {
  const { id } = useParams();
  return id ? <EingangDetail id={id} /> : <EingangListe />;
}

// ── Liste mit Upload und Kennzahl ───────────────────────────────────────────

function EingangListe() {
  const [status, setStatus] = useState('');
  const q = useQuery({ queryKey: ['eingang', status], queryFn: () => eingangsrechnungen({ status: status || undefined }) });
  const nav = useNavigate();
  const offen = (q.data?.eintraege ?? []).filter((e) => e.status === 'hochgeladen' || e.status === 'klaerfall' || e.status === 'geprueft').length;
  return (
    <section>
      <Seitenkopf titel="Eingangsrechnungen" unter={q.data ? `${q.data.gesamt} ${q.data.gesamt === 1 ? 'Rechnung' : 'Rechnungen'}${offen ? ` · ${offen} warten auf Prüfung bzw. Festschreiben` : ''}` : undefined} />
      <div className="er-oben">
        <Ablage onFertig={(d) => nav(`/belege/eingang/${d.id}`)} />
        <KostenProPaket />
      </div>
      <Filterleiste>
        <select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="er-filter-status">
          <option value="">Status: alle</option>
          {EINGANG_STATUS.map((s) => <option key={s} value={s}>{EINGANG_STATUS_TEXT[s]}</option>)}
        </select>
      </Filterleiste>
      <Zustand laden={q.isLoading} fehler={q.error}>
        <Tabelle testid="er-tabelle" zeilen={q.data?.eintraege ?? []} schluessel={(e) => e.id} leer="Noch keine Eingangsrechnung. Die DHL-Rechnung oben ablegen."
          onZeile={(e) => nav(`/belege/eingang/${e.id}`)}
          spalten={[
            { kopf: 'Rechnung', zelle: (e) => <span className="mono">{e.rechnungsnummer}</span> },
            { kopf: 'Lieferant', zelle: (e) => EINGANG_LIEFERANT_TEXT[e.lieferant] ?? e.lieferant_name },
            { kopf: 'Datum', zelle: (e) => datum(e.datum) },
            { kopf: 'Zeitraum', zelle: (e) => monat(e.leistungszeitraum) },
            { kopf: 'Pakete', zelle: (e) => e.pakete, rechts: true },
            { kopf: 'Netto', zelle: (e) => euro(e.netto_cents), rechts: true },
            { kopf: 'Brutto', zelle: (e) => euro(e.brutto_cents), rechts: true },
            { kopf: 'Status', zelle: (e) => <EingangStatusBadge status={e.status} /> },
            { kopf: 'Hochgeladen', zelle: (e) => <span className="hinweis">{datumZeit(e.hochgeladen_at)}</span> },
          ]} />
      </Zustand>
    </section>
  );
}

/** Ablagefeld: Drag & Drop oder Datei wählen. Prüft Typ und Größe schon im Browser; der Server prüft erneut. */
function Ablage({ onFertig }: { onFertig: (d: EingangsrechnungDetail) => void }) {
  const [ueber, setUeber] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<{ text: string; id?: string } | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  async function nimm(datei: File | undefined) {
    setFehler(null);
    if (!datei) return;
    if (datei.type && datei.type !== 'application/pdf') { setFehler({ text: `„${datei.name}“ ist kein PDF.` }); return; }
    if (datei.size > MAX_MB * 1024 * 1024) { setFehler({ text: `„${datei.name}“ ist größer als ${MAX_MB} MB.` }); return; }
    setLaeuft(true);
    try {
      const d = await eingangHochladen(datei);
      void qc.invalidateQueries({ queryKey: ['eingang'] });
      void qc.invalidateQueries({ queryKey: ['eingang-kennzahlen'] });
      onFertig(d);
    } catch (err) {
      const det = err instanceof ApiError ? (err.details as { eingangsrechnung_id?: string } | undefined) : undefined;
      setFehler({ text: (err as Error).message, id: det?.eingangsrechnung_id });
    } finally {
      setLaeuft(false);
      if (input.current) input.current.value = '';
    }
  }
  const drop = (e: DragEvent) => { e.preventDefault(); setUeber(false); void nimm(e.dataTransfer.files[0]); };
  return (
    <div className={`er-ablage${ueber ? ' ueber' : ''}${laeuft ? ' laeuft' : ''}`} data-testid="er-ablage"
      onDragOver={(e) => { e.preventDefault(); setUeber(true); }} onDragLeave={() => setUeber(false)} onDrop={drop}>
      <div className="er-ablage-symbol" aria-hidden>PDF</div>
      <div>
        <strong>{laeuft ? 'Rechnung wird gelesen und geprüft …' : 'DHL-Rechnung hier ablegen'}</strong>
        <p className="hinweis">
          Rechnung im DHL-Geschäftskundenportal als PDF herunterladen und hierher ziehen, oder{' '}
          <button type="button" className="knopf-link" onClick={() => input.current?.click()} disabled={laeuft}>Datei wählen</button>.
          {' '}Nur PDF, höchstens {MAX_MB} MB. StoreOS liest Kopf, Beträge und Positionen und prüft die Summen.
        </p>
        <input ref={input} type="file" accept="application/pdf,.pdf" hidden data-testid="er-datei" onChange={(e) => void nimm(e.target.files?.[0])} />
        {fehler && (
          <p className="fehler" role="alert" data-testid="er-upload-fehler">
            {fehler.text}{fehler.id && <> · <Link to={`/belege/eingang/${fehler.id}`}>vorhandene Rechnung öffnen</Link></>}
          </p>
        )}
      </div>
    </div>
  );
}

function Verteilung({ k }: { k: KostenMonat }) {
  const klassen = Object.entries(k.gewichtsklassen).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...klassen.map(([, n]) => n));
  return (
    <ul className="er-verteilung" aria-label="Pakete nach Gewichtsklasse">
      {klassen.map(([kl, n]) => (
        <li key={kl}><span>{kl}</span><span className="er-balken"><span style={{ width: `${(n / max) * 100}%` }} /></span><span className="rechts">{n}</span></li>
      ))}
    </ul>
  );
}

/** Kennzahl-Kachel „Kosten pro Paket“ (letzter Monat mit Rechnung, Vormonat zum Vergleich). */
export function KostenProPaket() {
  const q = useQuery({ queryKey: ['eingang-kennzahlen'], queryFn: eingangKennzahlen });
  const [akt, vor] = q.data?.monate ?? [];
  return (
    <Karte titel="Kosten pro Paket" klasse="er-kpi">
      <Zustand laden={q.isLoading} fehler={q.error}>
        {!akt && <p className="hinweis">Noch keine Rechnung. Nach dem ersten Upload steht hier, was ein Paket wirklich kostet (Porto, Zuschläge und anteilige Pauschale).</p>}
        {akt && (
          <div data-testid="er-kpi">
            <div className="er-kpi-wert"><strong>{euro(akt.je_paket_cents)}</strong> <span className="hinweis">je Paket · {monat(akt.monat)}</span></div>
            <dl className="werte">
              <dt>ohne Pauschale</dt><dd>{euro(akt.je_paket_ohne_pauschale_cents)}</dd>
              <dt>Anteil Pauschale</dt><dd>{prozent(akt.anteil_pauschale_bp)} ({euro(akt.pauschale_cents)})</dd>
              <dt>Pakete</dt><dd>{akt.pakete} · {akt.rechnungen} Rechnung{akt.rechnungen === 1 ? '' : 'en'}</dd>
              {vor && <><dt>Vormonat</dt><dd>{euro(vor.je_paket_cents)} je Paket ({monat(vor.monat)})</dd></>}
            </dl>
            <Verteilung k={akt} />
          </div>
        )}
      </Zustand>
    </Karte>
  );
}

// ── Detail ─────────────────────────────────────────────────────────────────

function EingangDetail({ id }: { id: string }) {
  const q = useQuery({
    queryKey: ['eingang-detail', id], queryFn: () => eingangsrechnung(id),
    // Nach dem Festschreiben übergibt der Beleg-Worker im Takt; so lange nachladen.
    refetchInterval: (d) => (d.state.data?.status === 'festgeschrieben' ? 3000 : false),
  });
  const qc = useQueryClient();
  const toast = useToast();
  const [dialog, setDialog] = useState<'pruefen' | 'verwerfen' | null>(null);
  const [grund, setGrund] = useState('');
  const d = q.data;
  const neu = (x: EingangsrechnungDetail, text: string) => {
    qc.setQueryData(['eingang-detail', id], x);
    void qc.invalidateQueries({ queryKey: ['eingang'] });
    void qc.invalidateQueries({ queryKey: ['eingang-kennzahlen'] });
    toast(text);
  };
  const fehlerBefunde = d?.pruefung.befunde.filter((b) => b.schwere === 'fehler') ?? [];
  const hinweise = d?.pruefung.befunde.filter((b) => b.schwere === 'hinweis') ?? [];

  return (
    <section>
      <p className="zurueck"><Link to="/belege/eingang">← Eingangsrechnungen</Link></p>
      <Zustand laden={q.isLoading} fehler={q.error}>
        {d && (
          <>
            <Seitenkopf titel={`${EINGANG_LIEFERANT_TEXT[d.lieferant] ?? d.lieferant_name} ${d.rechnungsnummer}`}
              unter={<span className="zeile"><EingangStatusBadge status={d.status} /><span className="hinweis">Rechnung vom {datum(d.datum)} · Leistungszeitraum {monat(d.leistungszeitraum)} · {d.pakete} Pakete</span></span>}>
              <Darf rolle="leitung">
                {(d.status === 'hochgeladen' || d.status === 'klaerfall') && (
                  <button type="button" className="knopf" data-testid="er-pruefen"
                    onClick={() => { if (d.status === 'klaerfall') { setGrund(''); setDialog('pruefen'); } else void eingangPruefen(id).then((x) => neu(x, 'Prüfung bestätigt')).catch((e) => toast((e as Error).message, 'fehler')); }}>
                    {d.status === 'klaerfall' ? 'Trotz Abweichung bestätigen …' : 'Prüfung bestätigen'}
                  </button>
                )}
                {d.status === 'geprueft' && (
                  <button type="button" className="knopf" data-testid="er-festschreiben"
                    onClick={() => void eingangFestschreiben(id).then((x) => neu(x, 'Festgeschrieben, Übergabe an BuchhaltungsButler eingereiht')).catch((e) => toast((e as Error).message, 'fehler'))}>
                    Festschreiben und an Buchhaltung
                  </button>
                )}
                {(d.status === 'hochgeladen' || d.status === 'klaerfall' || d.status === 'geprueft') && (
                  <button type="button" className="knopf-zweit" data-testid="er-verwerfen" onClick={() => { setGrund(''); setDialog('verwerfen'); }}>Verwerfen …</button>
                )}
              </Darf>
            </Seitenkopf>

            <Pruefergebnis d={d} fehler={fehlerBefunde} hinweise={hinweise} />

            <div className="raster-detail er-raster">
              <div>
                <Karte titel="Erkannte Daten">
                  <dl className="werte" data-testid="er-kopf">
                    <dt>Rechnungsnummer</dt><dd className="mono">{d.rechnungsnummer}</dd>
                    <dt>Rechnungsdatum</dt><dd>{datum(d.datum)}</dd>
                    <dt>Leistungszeitraum</dt><dd>{monat(d.leistungszeitraum)}</dd>
                    <dt>Einlieferung</dt><dd>{d.einlieferung_von ? `${datum(d.einlieferung_von)} bis ${datum(d.einlieferung_bis)}` : '–'}</dd>
                    <dt>Kundennummer</dt><dd className="mono">{d.kundennummer ?? '–'}</dd>
                    <dt>Abrechnungsnummer</dt><dd className="mono">{d.abrechnungsnummern.join(', ') || '–'}</dd>
                  </dl>
                  <dl className="summen" data-testid="er-summen">
                    <dt>Netto</dt><dd className="rechts">{euro(d.netto_cents)}</dd>
                    {d.steuer.map((s) => <Fragment key={s.kz}><dt>USt {s.kz} {(s.satz_bp / 100).toLocaleString('de-DE')} %</dt><dd className="rechts">{euro(s.betrag_cents)}</dd></Fragment>)}
                    <dt><strong>Brutto</strong></dt><dd className="rechts"><strong>{euro(d.brutto_cents)}</strong></dd>
                  </dl>
                </Karte>
                <Karte titel="Zusammenfassung laut Rechnung">
                  <Tabelle testid="er-zusammenfassung" zeilen={d.zusammenfassung} schluessel={(z) => z.produkt}
                    spalten={[
                      { kopf: 'Produkt / Service', zelle: (z) => z.produkt },
                      { kopf: 'Art', zelle: (z) => <span className={`er-kat er-kat-${z.kategorie}`}>{EINGANG_KATEGORIE_TEXT[z.kategorie]}</span> },
                      { kopf: 'Anzahl', zelle: (z) => z.anzahl, rechts: true },
                      { kopf: 'Gesamt', zelle: (z) => euro(z.gesamt_cents), rechts: true },
                    ]} />
                </Karte>
                <Abgleich id={id} />
              </div>
              <div>
                <PdfAnsicht id={id} name={`DHL-${d.rechnungsnummer}.pdf`} />
                <Buchhaltung d={d} />
              </div>
            </div>
            <Positionen d={d} />
          </>
        )}
      </Zustand>
      <Dialog offen={dialog === 'pruefen'} titel="Klärfall bestätigen" onSchliessen={() => setDialog(null)}>
        <DialogFormular absendenText="Als geprüft bestätigen" onAbbrechen={() => setDialog(null)} sperre={grund.trim().length < 5}
          onAbsenden={async () => { neu(await eingangPruefen(id, grund), 'Prüfung mit Begründung bestätigt'); setDialog(null); }}>
          <p className="hinweis">Die Abweichung bleibt in der Rechnung dokumentiert; StoreOS korrigiert keine Beträge. Warum ist die Rechnung trotzdem richtig?</p>
          <Feld label="Begründung (Pflicht)"><textarea value={grund} onChange={(e) => setGrund(e.target.value)} rows={3} data-testid="er-grund" /></Feld>
        </DialogFormular>
      </Dialog>
      <Dialog offen={dialog === 'verwerfen'} titel="Upload verwerfen" onSchliessen={() => setDialog(null)}>
        <DialogFormular absendenText="Verwerfen" gefahr onAbbrechen={() => setDialog(null)} sperre={grund.trim().length < 5}
          onAbsenden={async () => { neu(await eingangVerwerfen(id, grund), 'Verworfen'); setDialog(null); }}>
          <p className="hinweis">Die Datei bleibt mit Grund im Protokoll; dieselbe Rechnung kann danach neu hochgeladen werden.</p>
          <Feld label="Grund (Pflicht)"><textarea value={grund} onChange={(e) => setGrund(e.target.value)} rows={3} data-testid="er-grund-verwerfen" /></Feld>
        </DialogFormular>
      </Dialog>
    </section>
  );
}

function Pruefergebnis({ d, fehler, hinweise }: { d: EingangsrechnungDetail; fehler: EingangsrechnungDetail['pruefung']['befunde']; hinweise: EingangsrechnungDetail['pruefung']['befunde'] }) {
  return (
    <div className={`er-pruefung ${d.pruefung.ok ? 'ok' : 'abweichung'}`} data-testid="er-pruefung" data-ok={d.pruefung.ok}>
      <strong>{d.pruefung.ok
        ? 'Prüfung bestanden: Positionen = Zusammenfassung = netto, USt = netto × Satz, brutto = netto + USt.'
        : `Klärfall: ${fehler.length} Abweichung${fehler.length === 1 ? '' : 'en'}. Nichts wurde korrigiert.`}</strong>
      {fehler.length > 0 && <ul>{fehler.map((b, i) => <li key={i} data-code={b.code}>{b.text}</li>)}</ul>}
      {hinweise.length > 0 && <ul className="er-hinweise">{hinweise.map((b, i) => <li key={i} data-code={b.code}>{b.text}</li>)}</ul>}
      {d.pruef_grund && <p>Bestätigt{d.geprueft_von ? ` von ${d.geprueft_von}` : ''}: „{d.pruef_grund}“</p>}
      {d.verworfen_grund && <p>Verworfen: „{d.verworfen_grund}“</p>}
    </div>
  );
}

function Abgleich({ id }: { id: string }) {
  const q = useQuery({ queryKey: ['eingang-abgleich', id], queryFn: () => eingangAbgleich(id) });
  const k = (m: Record<string, number>) => Object.entries(m).map(([kl, n]) => `${n} × ${kl}`).join(', ') || '–';
  return (
    <Karte titel="Abgleich je Einlieferungstag">
      <Zustand laden={q.isLoading} fehler={q.error}>
        {q.data?.hinweis && <p className="hinweis-box" data-testid="er-abgleich-hinweis">{q.data.hinweis}</p>}
        <Tabelle testid="er-abgleich" zeilen={q.data?.tage ?? []} schluessel={(t) => t.datum} leer="Keine Einlieferungstage"
          spalten={[
            { kopf: 'Tag', zelle: (t) => datum(t.datum) },
            { kopf: 'DHL', zelle: (t) => <>{t.dhl_anzahl} <span className="hinweis">({k(t.dhl)})</span></> },
            { kopf: 'StoreOS', zelle: (t) => (q.data?.storeos_aktiv ? <>{t.storeos_anzahl} <span className="hinweis">({k(t.storeos)})</span></> : '–') },
            { kopf: 'Ergebnis', zelle: (t) => <span className={`badge er-abg-${t.ergebnis}`} title={t.text} data-ergebnis={t.ergebnis}>{ABGLEICH_TEXT[t.ergebnis]}</span> },
          ]} />
      </Zustand>
    </Karte>
  );
}

function Positionen({ d }: { d: EingangsrechnungDetail }) {
  return (
    <Karte titel={`Positionen (${d.positionen.length})`}>
      <Tabelle testid="er-positionen" zeilen={d.positionen} schluessel={(p) => String(p.nr)}
        spalten={[
          { kopf: 'Tag', zelle: (p) => (p.datum ? datum(p.datum) : p.abrechnungsnummer ? <span className="mono klein">{p.abrechnungsnummer}</span> : '–') },
          { kopf: 'Blatt', zelle: (p) => <span className="mono">{p.blatt ?? ''}</span> },
          { kopf: 'Produkt / Service', zelle: (p) => <>{p.produkt}{p.abschnitt === 'vorperiode' && <span className="badge warn"> Vorperiode</span>}</> },
          { kopf: 'Art', zelle: (p) => <span className={`er-kat er-kat-${p.kategorie}`}>{EINGANG_KATEGORIE_TEXT[p.kategorie]}</span> },
          { kopf: 'Anzahl', zelle: (p) => p.anzahl, rechts: true },
          { kopf: 'Einzel', zelle: (p) => euro(p.einzelpreis_cents), rechts: true },
          { kopf: 'Gesamt', zelle: (p) => euro(p.gesamt_cents), rechts: true },
          { kopf: 'USt', zelle: (p) => p.ust_kz ?? '' },
        ]} />
    </Karte>
  );
}

/** Original-PDF im iframe aus einer blob:-URL (Typ fest application/pdf); Link zum Öffnen/Speichern. */
function PdfAnsicht({ id, name }: { id: string; name: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => {
    let aktiv = true;
    let u: string | null = null;
    eingangPdfBlob(id).then((b) => { if (!aktiv) return; u = URL.createObjectURL(b); setUrl(u); }).catch((e) => setFehler((e as Error).message));
    return () => { aktiv = false; if (u) URL.revokeObjectURL(u); };
  }, [id]);
  return (
    <Karte titel="Original-PDF" aktion={url && <span className="zeile"><a href={url} target="_blank" rel="noopener" className="knopf-flach">Öffnen</a><a href={url} download={name} className="knopf-flach">Speichern</a></span>}>
      {fehler && <p className="fehler">{fehler}</p>}
      {url ? <iframe className="er-pdf" src={url} title="Original-PDF der Rechnung" data-testid="er-pdf" /> : !fehler && <p className="hinweis">PDF lädt …</p>}
    </Karte>
  );
}

function Buchhaltung({ d }: { d: EingangsrechnungDetail }) {
  const e = d.exporte[d.exporte.length - 1];
  const req = e?.request as { receipt?: { body?: Record<string, unknown> }; kontierung?: { geprueft: boolean; modus: string; rahmen: string } } | undefined;
  const ob = d.outbox[d.outbox.length - 1];
  return (
    <Karte titel="BuchhaltungsButler">
      {!ob && <p className="hinweis">{d.status === 'verworfen' ? 'Verworfen, wird nicht übergeben.' : 'Nach dem Festschreiben übergibt StoreOS PDF, Rechnungsnummer, Datum, Betrag und Lieferant als Eingangsrechnung.'}</p>}
      {ob && <p className="zeile" data-testid="er-outbox"><span className="mono klein">buchhaltung.eingangsrechnung</span><span className={`badge ob-${ob.status}`}>{ob.status}</span>{ob.letzter_fehler && <span className="fehler">{ob.letzter_fehler}</span>}</p>}
      {e && (
        <>
          <p className="zeile"><span className={`badge ${e.status === 'fehler' ? 'fehler-badge' : 'neutral'}`}>{e.status === 'uebertragen' ? 'übertragen' : 'Fehler'}</span> <span className="hinweis">{e.adapter} · {e.modus === 'mock' ? 'MOCK (nichts gesendet)' : e.modus.toUpperCase()}</span></p>
          {req?.receipt?.body && (
            <dl className="werte" data-testid="er-bhb">
              <dt>Typ</dt><dd className="mono">{String(req.receipt.body.type)}</dd>
              <dt>Lieferant</dt><dd>{String(req.receipt.body.counterparty)}</dd>
              <dt>Betrag</dt><dd>{String(req.receipt.body.amount)} EUR · USt {String(req.receipt.body.vat_rate)} %</dd>
              <dt>Beleg-ID</dt><dd className="mono">{d.bhb_receipt_id ?? '–'}</dd>
            </dl>
          )}
          {req?.kontierung && !req.kontierung.geprueft && <p className="warn-box">Kontierung {req.kontierung.rahmen} ist ein ungeprüfter Vorschlag (Steuerberatung). Modus: {req.kontierung.modus === 'nur_beleg' ? 'nur Beleg, BHB kontiert' : 'mit Buchungen'}.</p>}
          <details><summary>Request (JSON, ohne PDF)</summary><pre className="json">{JSON.stringify(e.request, null, 2)}</pre></details>
        </>
      )}
      <p className="hinweis klein">PDF sha256 <span className="mono">{d.pdf_sha256.slice(0, 16)}…</span> · {Math.round(d.pdf_bytes / 1024)} KB · Parser {d.parser} v{d.parser_version}</p>
    </Karte>
  );
}
